import React, { useMemo, useState } from 'react'
import Select from 'react-select'
import { Link } from 'react-router-dom'

import Modal from '../reusable/modal'
import Receive from '../reusable/receive'
import { Icd11ConceptPicker } from '../reusable/inputs'

import { patientName, useListData, usePet } from '../../modules'

const categoryLabels = {
    SURGICAL: 'Surgical',
    IMAGING: 'Imaging',
    RENAL: 'Renal',
    ONCOLOGY: 'Oncology',
    OPTICAL: 'Optical',
}

const statusClasses = {
    approved: 'bg-green-100 text-green-700',
    pending: 'bg-yellow-100 text-yellow-700',
    rejected: 'bg-red-100 text-red-700',
    expired: 'bg-gray-200 text-gray-700',
}

const PreauthCheck = ({ visible, toggle, patient, saleItem, onProceed, onCreated }) => {
    const [referenceNumber, setReferenceNumber] = useState('')
    const [doctorString, setDoctorString] = useState('')
    const [selectedScheme, setSelectedScheme] = useState(null)
    const [selectedDoctor, setSelectedDoctor] = useState(null)
    const [clinicalIndication, setClinicalIndication] = useState('')
    const [diagnosis, setDiagnosis] = useState(null)
    const [serviceStart, setServiceStart] = useState('')
    const [serviceEnd, setServiceEnd] = useState('')
    const [providerNotificationEmail, setProviderNotificationEmail] = useState('')
    const [sendDoctorConsent, setSendDoctorConsent] = useState(true)
    const [formFields, setFormFields] = useState({})
    const [result, setResult] = useState(null)

    const category = saleItem?.preauthCategory

    const [
        { loading: schemesLoading },
        { list: schemes = [] },
    ] = useListData('schemesByPatient', { id: patient?._id, string: '' }, { notify: true }, 'schemes', 100)

    const [
        { loading: employeesLoading },
        { list: employees = [] },
    ] = useListData('allEmployees', { string: doctorString }, { notify: true }, 'employees', 100)

    const [{ loading: searchLoading }, searchPreauths] = usePet('searchPreauths', {
        patient: patient?._id,
        category,
        referenceNumber,
        limit: 20,
    }, {
        notify: true,
        manual: true,
        callback: ({ data }) => {
            setResult(data?.preauths?.[0] ?? null)
        },
        load: { msg: 'Searching preauthorizations...' }
    })

    const [{ loading: createLoading }, createPreauth] = usePet('createPreauth', {
        patient: patient?._id,
        scheme: selectedScheme?.value,
        lineItem: saleItem?.lineItem,
        category,
        procedureName: saleItem?.name,
        estimatedAmount: saleItem?.price ?? 0,
        clinicalIndication,
        doctor: selectedDoctor?.value,
        diagnosisCode: diagnosis?.code,
        serviceStart: serviceStart || undefined,
        serviceEnd: serviceEnd || undefined,
        providerNotificationEmail: providerNotificationEmail || undefined,
        formFields,
        sendDoctorConsent,
        attachments: [],
    }, {
        notify: true,
        manual: true,
        callback: ({ data }, notif, ops) => {
            notif('Preauthorization request has been created', ops('success'))
            setResult(data?.preauth ?? null)
            if (data?.preauth?.shaStatus === 'FINALISED') {
                onProceed(data?.preauth)
                return
            }

            if (onCreated) onCreated(data?.preauth)
            else toggle()
        },
        load: { msg: 'Creating preauthorization...' }
    })

    const schemeOptions = useMemo(() =>
        schemes
            .filter(scheme => scheme?.active !== false && scheme?.scheme?.name !== 'Cash')
            .map(scheme => ({
                label: `${scheme?.scheme?.name}${scheme?.main ? ' (Main)' : ''}`,
                value: scheme?._id,
                ...scheme,
            })),
    [schemes])

    const doctorOptions = useMemo(() =>
        employees.map(employee => ({
            label: employee?.name,
            value: employee?._id,
            ...employee,
        })),
    [employees])

    const updateField = (key, value) => setFormFields(current => ({ ...current, [key]: value }))
    const canCreatePreauth = Boolean(saleItem?.lineItem && selectedScheme && selectedDoctor && diagnosis?.code)

    return (
        <Modal
            title="Pre-Authorization Required"
            visible={visible}
            toggle={toggle}
            w={700}>
            <div className="space-y-5">
                <div className="rounded border border-yellow-200 bg-yellow-50 p-4">
                    <p className="font-semibold mb-1">{saleItem?.name}</p>
                    <p className="text-sm mb-1">Category: {categoryLabels[category] ?? category ?? 'Not specified'}</p>
                    <p className="text-sm text-gray-700 mb-0">This service needs a preauthorization before we add it to {patientName(patient?.name)}’s bill.</p>
                </div>

                <div className="rounded border border-blue-200 bg-blue-50 p-4">
                    <p className="text-xs font-semibold text-blue-800 uppercase mb-1">How to use this step</p>
                    <p className="text-sm text-blue-900 mb-2">
                        This screen is for immediate billing-time interruption handling: attach an existing usable SHA approval or request one if this service can continue from the current visit context.
                    </p>
                    <p className="text-xs text-blue-800 mb-0">
                        For elective or longer-running follow-up work, use the SHA preauthorization workbench instead.
                    </p>
                    <div className="mt-3">
                        <Link to="/app/accounts/sales/sha-preauths">
                            <button className="btn bg-white border border-blue-200 text-blue-800 hover:bg-blue-100">
                                Open SHA Preauth Workbench
                            </button>
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div className="rounded border border-gray-200 p-4">
                        <p className="font-semibold mb-3">Attach existing approval</p>
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                            Reference Number
                        </label>
                        <input
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                            value={referenceNumber}
                            onChange={e => setReferenceNumber(e.target.value)}
                            placeholder="SHA_PA_12345678" />
                        <div className="flex justify-end">
                            <button
                                disabled={!referenceNumber?.trim() || searchLoading}
                                onClick={searchPreauths}
                                className="text-white hisani-btn-green-light font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                Search
                            </button>
                        </div>

                        {
                            referenceNumber?.trim() &&
                            <Receive loading={searchLoading}>
                                {
                                    result ?
                                        <div className="mt-4 rounded border border-gray-200 p-3">
                                            <div className="flex items-center justify-between gap-3 mb-2">
                                                <p className="font-semibold mb-0">{result?.referenceNumber}</p>
                                                <span className={`px-2 py-1 rounded text-xs font-semibold ${statusClasses[result?.status] ?? statusClasses.pending}`}>
                                                    {result?.status ?? 'pending'}
                                                </span>
                                            </div>
                                            <p className="text-sm mb-1">{result?.procedureName}</p>
                                            <p className="text-sm text-gray-600 mb-3">
                                                Approved Amount: Ksh. {result?.approvedAmount ?? result?.estimatedAmount ?? 0}
                                            </p>
                                            {
                                                result?.consumedAt &&
                                                <p className="text-xs text-red-700 mb-3">
                                                    This preauthorization was already consumed on {new Date(result.consumedAt).toLocaleString()} and cannot be reused.
                                                </p>
                                            }
                                            {
                                                !result?.consumedAt && result?.isElective && result?.scheduledVisitDate && new Date(result.scheduledVisitDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0) &&
                                                <p className="text-xs text-yellow-700 mb-3">
                                                    This elective preauthorization is scheduled for {new Date(result.scheduledVisitDate).toLocaleString()} and should be used on or after that date.
                                                </p>
                                            }
                                            {
                                                !result?.consumedAt && result?.shaStatus !== 'FINALISED' &&
                                                <p className="text-xs text-yellow-700 mb-3">
                                                    This preauthorization exists, but it must be SHA-finalised before it can be attached to billing.
                                                </p>
                                            }
                                            <div className="text-right">
                                                <button
                                                    disabled={Boolean(result?.consumedAt) || (result?.isElective && result?.scheduledVisitDate && new Date(result.scheduledVisitDate).setHours(0, 0, 0, 0) > new Date().setHours(0, 0, 0, 0)) || result?.shaStatus !== 'FINALISED'}
                                                    onClick={() => onProceed(result)}
                                                    className="text-white hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                                    Attach to Item
                                                </button>
                                            </div>
                                        </div>
                                        :
                                        <p className="text-sm text-gray-500 mt-4 mb-0">No preauthorization matched that reference number yet.</p>
                                }
                            </Receive>
                        }
                    </div>

                    <div className="rounded border border-gray-200 p-4">
                        <p className="font-semibold mb-3">Request a new preauth</p>
                        {
                            !saleItem?.lineItem &&
                            <div className="rounded border border-yellow-200 bg-yellow-50 px-3 py-3 mb-4">
                                <p className="text-xs font-semibold text-yellow-800 mb-1">Creation path limited in this flow</p>
                                <p className="text-sm text-yellow-800 mb-0">
                                    We can attach an existing SHA approval here, but creating a new SHA preauthorization needs the billed claim line from the current visit or from the dedicated preauthorization work surface first.
                                </p>
                            </div>
                        }
                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                            Patient Scheme
                        </label>
                        <Select
                            value={selectedScheme}
                            onChange={setSelectedScheme}
                            isLoading={schemesLoading}
                            options={schemeOptions}
                            placeholder="Select scheme" />

                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                            Doctor
                        </label>
                        <Select
                            value={selectedDoctor}
                            onInputChange={setDoctorString}
                            onChange={setSelectedDoctor}
                            isLoading={employeesLoading}
                            options={doctorOptions}
                            placeholder="Select doctor" />

                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                            Clinical Indication
                        </label>
                        <textarea
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 mb-3 leading-tight focus:outline-none focus:bg-white"
                            rows={4}
                            value={clinicalIndication}
                            onChange={e => setClinicalIndication(e.target.value)}
                            placeholder="Optional clinical notes for the request" />

                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                            Primary ICD-11 Diagnosis
                        </label>
                        <Icd11ConceptPicker
                            toolId={ `preauth-${patient?._id ?? 'patient'}-${saleItem?.lineItem ?? saleItem?._id ?? 'item'}` }
                            value={ diagnosis }
                            onChange={ setDiagnosis }
                            placeholder="Search ICD-11 diagnosis" />

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                            <div>
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                    Service Start
                                </label>
                                <input
                                    type="datetime-local"
                                    value={ serviceStart }
                                    onChange={ e => setServiceStart(e.target.value) }
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                            </div>
                            <div>
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                    Service End
                                </label>
                                <input
                                    type="datetime-local"
                                    value={ serviceEnd }
                                    onChange={ e => setServiceEnd(e.target.value) }
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                            </div>
                        </div>

                        <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                            Provider Notification Email
                        </label>
                        <input
                            type="email"
                            value={ providerNotificationEmail }
                            onChange={ e => setProviderNotificationEmail(e.target.value) }
                            placeholder="Optional notification email"
                            className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />

                        {
                            category === 'SURGICAL' &&
                            <>
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                                    Surgery Date
                                </label>
                                <input
                                    type="datetime-local"
                                    value={ formFields?.surgery_date ?? '' }
                                    onChange={ e => updateField('surgery_date', e.target.value) }
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                            </>
                        }

                        {
                            category === 'IMAGING' &&
                            <>
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                                    Imaging Clinical Indications
                                </label>
                                <textarea
                                    rows={3}
                                    value={ formFields?.clinical_indications ?? '' }
                                    onChange={ e => updateField('clinical_indications', e.target.value) }
                                    placeholder="Clinical indications for imaging"
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                            </>
                        }

                        {
                            category === 'RENAL' &&
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                <div>
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Sessions Required
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={ formFields?.number_of_sessions_required ?? '' }
                                        onChange={ e => updateField('number_of_sessions_required', e.target.value) }
                                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                                </div>
                                <div>
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Cost Per Session
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={ formFields?.cost_per_session ?? '' }
                                        onChange={ e => updateField('cost_per_session', e.target.value) }
                                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                                </div>
                            </div>
                        }

                        {
                            category === 'ONCOLOGY' &&
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-4">
                                <div>
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Sessions Required
                                    </label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={ formFields?.number_of_sessions_required ?? '' }
                                        onChange={ e => updateField('number_of_sessions_required', e.target.value) }
                                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                                </div>
                                <div>
                                    <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2">
                                        Cost Per Session
                                    </label>
                                    <input
                                        type="number"
                                        min="0"
                                        value={ formFields?.cost_per_session ?? '' }
                                        onChange={ e => updateField('cost_per_session', e.target.value) }
                                        className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                                </div>
                            </div>
                        }

                        {
                            category === 'OPTICAL' &&
                            <>
                                <label className="block uppercase tracking-wide text-gray-700 text-xs font-bold mb-2 mt-4">
                                    Lens Amount
                                </label>
                                <input
                                    type="number"
                                    min="0"
                                    value={ formFields?.lens_amount ?? '' }
                                    onChange={ e => updateField('lens_amount', e.target.value) }
                                    className="appearance-none block w-full bg-gray-200 text-gray-700 border border-gray-200 rounded py-3 px-4 leading-tight focus:outline-none focus:bg-white" />
                            </>
                        }

                        <label className="flex items-center mt-4">
                            <input
                                type="checkbox"
                                checked={ sendDoctorConsent }
                                onChange={ e => setSendDoctorConsent(e.target.checked) }
                                className="form-checkbox cursor-pointer h-4 w-4 mr-2" />
                            <span className="text-sm text-gray-700">Send doctor consent request automatically when required</span>
                        </label>

                        <div className="flex justify-end">
                            <button
                                disabled={!canCreatePreauth || createLoading}
                                onClick={createPreauth}
                                className="text-white hisani-btn-orange font-medium rounded-lg text-sm px-5 py-2.5 text-center">
                                Request Preauth
                            </button>
                        </div>
                        <p className="text-xs text-gray-500 mt-3 mb-0">
                            New preauthorization requests stay blocked until they are approved. If the case is elective or needs follow-up outside this visit, move it to the SHA preauthorization workbench.
                        </p>
                    </div>
                </div>
            </div>
        </Modal>
    )
}

export default PreauthCheck
